import { Edit01, FilterLines, InfoCircle, Package, Plus, SearchLg, SwitchVertical01, Trash01, XClose } from '@untitled-ui/icons-react/build/cjs';
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CardCmp from 'src/components/CardCmp';
import DeleteModal from 'src/components/DeleteModal';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import SelectWithActionTooltip from 'src/components/SelectWithActionTooltip';
import CreateViewModal from 'src/components/TableTabButton/CreateViewModal';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { ORDER_STATUS } from 'src/constants/common';
import { useAddressDetails } from 'src/hooks/useAddressDetails';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { createDateWithoutTimezoneConversion, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';

import { TRACK_FILTER_SERVICE_ARR, TRACK_SORT_BY } from './__mocks__/track.mock';
import useTrackViews from './hooks/useTrackViews';
import TrackFilterModal from './modals/TrackFilterModal';
import TrackSidebarLoading from './skeletons/TrackSidebarLoading';
import { makeTrackEmptyStateTitleDesc } from './track.emptyState';
import { TRACK_VIEW_SORT_DEFAULTS } from './track.helpers';
import { readTrackSectionOpen, writeTrackSectionOpen } from './track.preferences';
import { trackList as trackListSelector, trackLoading, setTrackParams, trackParams as trackParamsSelector } from './track.slice';
import { normalizeTrackSearchQuery } from './track.utils';
import TrackDateRangeQuickPicker from './TrackDateRangeQuickPicker';
import TrackOrderCard from './TrackOrderCard';
import TrackOverviewStats from './TrackOverviewStats';
import TrackViewDropdown from './TrackViewDropdown';

const TrackLeftSidebar = ({ onOrderHover, onOrderHoverEnd }: { onOrderHover?: (order: any) => void; onOrderHoverEnd?: () => void }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const trackList = useAppSelector(trackListSelector);
    const isLoading = useAppSelector(trackLoading);
    const trackParams = useAppSelector(trackParamsSelector);
    const currentUser = useAppSelector(currentUserSelector);
    const overviewUserId: number | string | undefined = currentUser?.id;
    // CardCmp owns `isOpen` internally and reads `defaultOpen` only on its FIRST
    // render, but the stored value is namespaced by user id. `auth` is redux-persisted
    // so the id is normally known on first render; if it lands later, keying the card
    // on the id (below) remounts it once so it boots with the saved state.
    const overviewDefaultOpen = useMemo(() => readTrackSectionOpen(overviewUserId, 'overview'), [overviewUserId]);
    // Timezone lives on basic.appearance, not on auth — reading state.auth.timezone silently
    // yields undefined and falls back to UTC, which shifts "today" a day for western zones.
    const userTimezone = useAppSelector((state) => state.basic.appearance?.timezone) || 'UTC';
    const search = useMemo(() => trackParams.search ?? '', [trackParams.search]);
    const [searchInput, setSearchInput] = useState(search);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    // How the filter modal was opened: "+" / Add Filters → 'create' (its Save View creates a
    // view); the Overview card's Edit View → 'edit' (its button overwrites the active view).
    const [filterModalViewMode, setFilterModalViewMode] = useState<'create' | 'edit'>('create');

    const openFilterModal = (mode: 'create' | 'edit') => {
        setFilterModalViewMode(mode);
        setIsFilterModalOpen(true);
    };

    // Saved views — same state machine the table view tabs use (useTrackViews), surfaced here
    // as the Overview card's title dropdown + footer actions, so both views stay in sync.
    const {
        customViews,
        activeViewTab,
        activeCustomView,
        isSaveEnable,
        isViewChangeLoading,
        isError,
        setIsError,
        viewAction,
        viewModalText,
        viewNames,
        applyView,
        applyDefaultTab,
        applyModalFilters,
        openRenameView,
        openDeleteView,
        closeViewAction,
        saveActiveView,
        handleViewModalSave,
        handleDeleteModalClose,
    } = useTrackViews();
    const viewNameRef = useRef<HTMLHeadingElement>(null);

    const OVERVIEW_OPTION_VALUE = 'overview';
    const viewSelectorOptions = useMemo(
        () => [{ label: String(t('Common.Overview')), value: OVERVIEW_OPTION_VALUE }, ...customViews.map((view) => ({ label: view.name, value: view.value }))],
        [customViews, t]
    );

    // Overview = no saved view: reset every filter to the stage's defaults (the view's
    // filters must not leak into the plain overview). A view = apply its saved filters.
    const handleViewSelect = (value: string) => {
        if (value === OVERVIEW_OPTION_VALUE) {
            if (activeCustomView) applyDefaultTab(trackParams.status, { fullReset: true });

            return;
        }

        const view = customViews.find((item) => item.value === value);

        if (view) applyView(view);
    };

    const { shipperOptions, consigneeOptions } = useAddressDetails();
    const hasAdvancedFilters = useMemo(() => !!(trackParams.shipperId || trackParams.consigneeId || trackParams.originSearch || trackParams.destinationSearch || trackParams.refSearch), [trackParams]);

    // Keyed on the committed search ONLY, exactly like WAL (`if (isParams?.search)`). Advanced
    // filters deliberately do NOT switch to the search copy: WAL keeps showing the stage message
    // ("Nothing Scheduled for Pick Up Today") when a filter empties the list.
    const emptyState = useMemo(() => makeTrackEmptyStateTitleDesc(trackParams, !!search, userTimezone, t), [search, trackParams, userTimezone, t]);

    const activeFilterChips = useMemo(() => {
        const chips: { key: string; label: string; onRemove: () => void }[] = [];

        if (trackParams.serviceType && trackParams.serviceType !== 'all') {
            const serviceLabel = TRACK_FILTER_SERVICE_ARR.find((o) => o.value === trackParams.serviceType)?.label ?? trackParams.serviceType;
            chips.push({ key: 'serviceType', label: serviceLabel, onRemove: () => dispatch(setTrackParams({ serviceType: 'all', page: 1 })) });
        }

        if (trackParams.shipperId) {
            const name = (shipperOptions as any[])?.find((o) => String(o.value) === String(trackParams.shipperId))?.label ?? 'Shipper';
            chips.push({ key: 'shipperId', label: `From: ${name}`, onRemove: () => dispatch(setTrackParams({ shipperId: null, page: 1 })) });
        }

        if (trackParams.consigneeId) {
            const name = (consigneeOptions as any[])?.find((o) => String(o.value) === String(trackParams.consigneeId))?.label ?? 'Consignee';
            chips.push({ key: 'consigneeId', label: `To: ${name}`, onRemove: () => dispatch(setTrackParams({ consigneeId: null, page: 1 })) });
        }

        if (trackParams.refSearch) {
            chips.push({ key: 'refSearch', label: `Ref: ${trackParams.refSearch}`, onRemove: () => dispatch(setTrackParams({ refSearch: '', page: 1 })) });
        }

        if (trackParams.originSearch) {
            chips.push({ key: 'originSearch', label: `From: ${trackParams.originSearch}`, onRemove: () => dispatch(setTrackParams({ originSearch: '', page: 1 })) });
        }

        if (trackParams.destinationSearch) {
            chips.push({ key: 'destinationSearch', label: `To: ${trackParams.destinationSearch}`, onRemove: () => dispatch(setTrackParams({ destinationSearch: '', page: 1 })) });
        }

        if (trackParams.startDate) {
            chips.push({
                key: 'startDate',
                label: `From: ${getFormattedDateIgnoringTime(trackParams.startDate, 'MMM Do, YYYY')}`,
                onRemove: () => dispatch(setTrackParams({ startDate: '', page: 1 })),
            });
        }

        if (trackParams.endDate) {
            chips.push({ key: 'endDate', label: `To: ${getFormattedDateIgnoringTime(trackParams.endDate, 'MMM Do, YYYY')}`, onRemove: () => dispatch(setTrackParams({ endDate: '', page: 1 })) });
        }

        return chips;
    }, [trackParams, shipperOptions, consigneeOptions]);

    // Applied-filter count for the "+" badge. Service / Division / Billing Account are excluded
    // because each already has its own always-visible inline dropdown, and the date range is
    // excluded because it is always set to something (today by default) — counting either would
    // show a filter the user never applied here.
    const appliedFilterCount = useMemo(
        () => activeFilterChips.filter((chip) => !['serviceType', 'divisionId', 'billingAccountId', 'startDate', 'endDate'].includes(chip.key)).length,
        [activeFilterChips]
    );

    // Temporary: the applied-filter chips row is hidden in favour of the "+" badge alone.
    // Mirrors hasAdvancedFilterTemporaryHide in wal-frontend-v2's MapLeftSidebarViewNew — grep
    // that name across both repos when the chips row is brought back.
    const hasAdvancedFilterTemporaryHide = true;

    // Temporary: the funnel button in the search row is hidden because it has no behaviour yet —
    // the advance-filter modal is reached through the "+" badge below it. Flip this to false (and
    // give the button an onClick) when the funnel gets its own action.
    const hasFilterButtonTemporaryHide = true;

    const handleClearAllAdvancedFilters = () => {
        dispatch(
            setTrackParams({
                serviceType: 'all',
                shipperId: null,
                consigneeId: null,
                refSearch: '',
                originSearch: '',
                destinationSearch: '',
                filterBySubStatus: '',
                startDate: moment().tz(userTimezone).format('YYYY-MM-DD'),
                endDate: moment().tz(userTimezone).format('YYYY-MM-DD'),
                page: 1,
            })
        );
    };

    const divisionOptions = useMemo(() => (currentUser?.divisions ?? []).map((div: any) => ({ value: div.id, label: div.displayName })), [currentUser?.divisions]);

    const billingAccountOptions = useMemo(() => {
        const divisions: any[] = currentUser?.divisions ?? [];
        const source = trackParams.divisionId ? divisions.filter((div: any) => div.id === trackParams.divisionId) : divisions;

        return source.flatMap((div: any) =>
            (div.customerBillingAccount ?? []).map((acc: any) => ({
                value: acc.id,
                label: acc.displayName,
            }))
        );
    }, [currentUser?.divisions, trackParams.divisionId]);

    const dispatchSearch = useCallback(
        debounce((value: string) => dispatch(setTrackParams({ search: value })), 300),
        []
    );

    const handleSearchChange = (e: any) => {
        const value = e?.target?.value ?? '';
        const query = normalizeTrackSearchQuery(value);
        setSearchInput(value);

        if (query || value === '') {
            dispatchSearch(query);
        }
    };

    const handleClearSearch = () => {
        setSearchInput('');
        dispatchSearch('');
    };

    // Active whenever a sort from this menu is in effect — i.e. whenever the dropdown shows a
    // checkmark, the default (Last Update Time) included. WAL's trigger keys off "not the default"
    // instead, but its default (createdAt) isn't one of the menu options, so there it means the
    // same thing: a checked item == a highlighted button. Ours lists the default, so comparing
    // against it would leave the button grey while an option sits visibly checked.
    const isSortApplied = TRACK_SORT_BY.some((option) => option.value === (trackParams.sortField || TRACK_VIEW_SORT_DEFAULTS.sortField));

    const handleSortSelect = (optionValue: string) => {
        dispatch(setTrackParams({ sortField: trackParams.sortField === optionValue ? 'lastUpdatedAt' : optionValue }));
    };

    const handleServiceTypeChange = (e: any) => {
        dispatch(setTrackParams({ serviceType: e?.value ?? 'all' }));
    };

    const handleDivisionChange = (e: any) => {
        dispatch(setTrackParams({ divisionId: e?.value ?? null, billingAccountId: null }));
    };

    const handleBillingAccountChange = (e: any) => {
        dispatch(setTrackParams({ billingAccountId: e?.value ?? null }));
    };

    return (
        <>
            {/* ── Search + Sort + Filter card ── */}
            <div className="flex flex-col bg-white shadow-md rounded-lg flex-none">
                {/* Search row */}
                <div className="flex gap-x-2 py-2 px-4 border-b border-utilityGray200">
                    <InputText
                        inputName="searchTrackOrder"
                        placeholder="Search"
                        className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input"
                        icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                        value={searchInput}
                        size="sm"
                        inputType="text"
                        onChangeFunc={handleSearchChange}
                        parentClassName="flex-1"
                        isClearable={true}
                    />
                    {/* selectTooltipParentClassName: ActionTooltip pins the panel with an inline `top: 0px`,
                        so it opens flush against the trigger. The `!` prefix is what lets the class win over
                        that inline style — same 4px offset WAL's sort trigger uses (MapLeftSidebarViewNew). */}
                    <SelectWithActionTooltip
                        isOpenChildTopPosOnTopFalse={true}
                        actionTooltipSelectWrap="min-w-[170px]"
                        selectTooltipPosition="right"
                        selectTooltipParentClassName="!top-1"
                        selectBoxHeading={true}
                        selectBoxHeadingLabel="Sort by"
                        selectedOptionValue={trackParams.sortField}
                        onSelectOption={handleSortSelect}
                        label={
                            <TooltipCmp message={t('Common.Sort_by', 'Sort by')} childrenClassName="flex">
                                {/* Active state while a sort from this menu is applied, so it reads as "set"
                                    without opening the dropdown — same two class sets as WAL radar's sort
                                    trigger in MapLeftSidebarViewNew. */}
                                <ButtonCmp className={isSortApplied ? 'btn-outline-primary !bg-primary50' : 'btn_secondary_black'} size="square-sm" icon={<SwitchVertical01 className="w-4 h-4" />}>
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>
                        }
                        options={TRACK_SORT_BY}
                    />

                    {!hasFilterButtonTemporaryHide && (
                        <ButtonCmp className="btn_secondary_black" size="square-sm" icon={<FilterLines className="w-4 h-4" />}>
                            <></>
                        </ButtonCmp>
                    )}
                </div>

                {/* Filter row */}
                {hasAdvancedFilters && !hasAdvancedFilterTemporaryHide ? (
                    <div className="flex flex-col gap-y-1.5 py-2 px-4">
                        {/* Header: count + actions */}
                        <div className="flex items-center justify-between gap-x-2">
                            <span className="text-xs leading-[18px] font-medium text-grayLight900 whitespace-nowrap">
                                {activeFilterChips.length} Filter{activeFilterChips.length !== 1 ? 's' : ''} Applied
                            </span>
                            <div className="flex items-center gap-x-3">
                                <ButtonCmp className="btn-link-secondary !px-0 !py-0" onClick={() => openFilterModal('create')} icon={<Plus className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />}>
                                    Add Filters
                                </ButtonCmp>
                                <ButtonCmp className="btn-link-outline-primary !px-0 !py-0" onClick={handleClearAllAdvancedFilters} icon={<XClose className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />}>
                                    Clear All
                                </ButtonCmp>
                            </div>
                        </div>
                        {/* Chips */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {activeFilterChips.map((chip) => (
                                <BadgeCmp key={chip.key} style="modern" type="gray" isHidePillDot mainClassName="whitespace-nowrap gap-x-1">
                                    <span>{chip.label}</span>
                                    <XClose className="w-3 h-3 min-w-3 ml-1 cursor-pointer flex-none text-grayLight500 hover:text-grayLight900" onClick={chip.onRemove} />
                                </BadgeCmp>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-x-1.5 flex-wrap gap-y-1.5 py-2 px-4">
                        <SelectBox
                            parentClassName={`flex-1 min-w-[120px] filter-view-selectbox filter-view-selectbox-custom filter-selectbox-with-no-value ${
                                trackParams.serviceType && trackParams.serviceType !== 'all' ? 'filter-selectbox-with-value' : ''
                            }
                           `}
                            name="serviceType"
                            isShowTooltipOnTruncate
                            size="sm"
                            id="serviceType"
                            className="form_control !text-xs"
                            isClearable={true}
                            placeholder="All Services"
                            isSearchable={false}
                            options={TRACK_FILTER_SERVICE_ARR}
                            onChangeFunc={handleServiceTypeChange}
                            value={TRACK_FILTER_SERVICE_ARR.find((item) => item.value === trackParams.serviceType)}
                        />
                        <SelectBox
                            parentClassName={`flex-1 min-w-[95px] filter-view-selectbox filter-view-selectbox-custom ${
                                trackParams.divisionId ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                            }`}
                            name="division"
                            isShowTooltipOnTruncate
                            id="division"
                            size="sm"
                            className="form_control !text-xs"
                            isClearable={true}
                            isSearchable={false}
                            placeholder="Division"
                            options={divisionOptions}
                            onChangeFunc={handleDivisionChange}
                            value={divisionOptions.find((opt: any) => opt.value === trackParams.divisionId) ?? null}
                        />
                        <SelectBox
                            parentClassName={`flex-1 min-w-[95px] filter-view-selectbox filter-view-selectbox-custom ${
                                trackParams.billingAccountId ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                            }`}
                            name="billingAccount"
                            isShowTooltipOnTruncate
                            id="billingAccount"
                            size="sm"
                            className="form_control !text-xs"
                            isClearable={true}
                            isSearchable={false}
                            isDisabled={!trackParams.divisionId}
                            placeholder="Billing Account"
                            options={billingAccountOptions}
                            onChangeFunc={handleBillingAccountChange}
                            value={billingAccountOptions.find((opt) => opt.value === trackParams.billingAccountId) ?? null}
                        />
                        {/* The applied-filter count rides in as a second icon so it anchors to the
                            button box itself (hence `relative` on the button). At 8px rather than 9px
                            a descender digit (3, 9) still reads as centred in a 16px circle, and
                            min-w + px rather than a fixed w-4 lets a two-digit count widen the pill
                            instead of overflowing it. */}
                        <TooltipCmp message={t('Radar.Advance_Filter', 'Advance Filter')} childrenClassName="flex">
                            <ButtonCmp
                                className="btn_secondary_black flex-none relative"
                                size="square-sm"
                                icon={
                                    <>
                                        <Plus className="w-4 h-4" />
                                        {appliedFilterCount > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-[3px] bg-danger600 text-white text-[8px] font-bold rounded-full inline-flex items-center justify-center leading-none border border-white pointer-events-none">
                                                {appliedFilterCount}
                                            </span>
                                        )}
                                    </>
                                }
                                onClick={() => openFilterModal('create')}
                            >
                                <></>
                            </ButtonCmp>
                        </TooltipCmp>
                    </div>
                )}
            </div>

            {/* ── Overview Stats Card ── */}
            <CardCmp
                key={`overview-${overviewUserId ?? 'anon'}`}
                // With saved views the title becomes a dropdown ("Overview" + the same view list
                // as the table view tabs); without views the plain title renders instead.
                headerTitle={customViews.length > 0 ? '' : 'Overview'}
                headerLeftSideContent={
                    customViews.length > 0 ? (
                        <TrackViewDropdown
                            options={viewSelectorOptions}
                            selectedValue={activeCustomView ? activeViewTab : OVERVIEW_OPTION_VALUE}
                            onSelect={handleViewSelect}
                            dropdownPosition="left"
                            // max-w-full (NOT w-full): content-width capped to the header, so a short
                            // name doesn't claim the whole row (the empty space beside it still hits the
                            // card's accordion toggle) while a long name caps + truncates.
                            containerClassName="relative flex min-w-0 max-w-full"
                            triggerClassName="min-w-0 max-w-full"
                            // noTooltip while open: the tooltip wrapper re-renders the h5 outside the
                            // truncate flow and breaks the ellipsis on click.
                            labelContent={(isViewMenuOpen: boolean) => (
                                <TruncateTextCmp
                                    refName={viewNameRef}
                                    message={activeCustomView?.name || String(t('Common.Overview'))}
                                    truncateTextParentClassName="flex-1 min-w-0"
                                    noTooltip={isViewMenuOpen}
                                >
                                    <h5 className="table-title truncate" ref={viewNameRef}>
                                        {activeCustomView?.name || String(t('Common.Overview'))}
                                    </h5>
                                </TruncateTextCmp>
                            )}
                            listItemClassName="!px-1.5 !py-1"
                        />
                    ) : undefined
                }
                isAccordion
                specificSectionAccordion
                accordionIconType="plus-minus"
                defaultOpen={overviewDefaultOpen}
                onAccordionToggle={(isOpen) => writeTrackSectionOpen(overviewUserId, 'overview', isOpen)}
                headerParentClassName="!border-b-0 px-4 radar-card-title"
                cardClassName="!border-0 !rounded-lg flex-none"
                childrenClassName="px-4 pb-3"
                isCardContentWithoutSpacing
                headerRightSideContent={
                    <div className="flex items-center gap-x-1" onClick={(e) => e.stopPropagation()}>
                        {/* Quick date-range control (WAL radar port) — disabled on In Transit,
                            where the backend ignores the date filter entirely. */}
                        <TrackDateRangeQuickPicker disabled={trackParams.status === ORDER_STATUS.IN_TRANSIT} />
                    </div>
                }
                // View actions — only while a saved view is selected in the dropdown.
                isFooter={!!activeCustomView}
                footerParentClassName="!py-2.5 !px-4"
                footerLeftSideContent={
                    activeCustomView ? (
                        <ButtonCmp
                            className="text-xs leading-[18px] font-semibold text-grayLight600 !shadow-none !p-0"
                            size="none"
                            icon={<InfoCircle className="w-4 h-4 flex-none" />}
                            disabled={isViewChangeLoading}
                            onClick={openRenameView}
                        >
                            {t('QuotingHub.QuotingDashboard.Rename_View')}
                        </ButtonCmp>
                    ) : undefined
                }
                footerRightSideContent={
                    activeCustomView ? (
                        <div className="flex items-center gap-x-4" onClick={(e) => e.stopPropagation()}>
                            {/* Save View — text only. Enabled only when the live filters differ from
                                the selected view; overwrites it directly (no name popup). */}
                            <ButtonCmp
                                className="text-xs leading-[18px] font-semibold text-grayLight600 !shadow-none !p-0"
                                size="none"
                                loading={isViewChangeLoading}
                                disabled={!isSaveEnable || isViewChangeLoading}
                                onClick={() => void saveActiveView()}
                            >
                                {t('Common.Save_View')}
                            </ButtonCmp>
                            <ButtonCmp
                                className="text-xs leading-[18px] font-semibold text-grayLight600 !shadow-none !p-0"
                                size="none"
                                icon={<Edit01 className="w-4 h-4 flex-none" />}
                                disabled={isViewChangeLoading}
                                onClick={() => openFilterModal('edit')}
                            >
                                {t('QuotingHub.QuotingDashboard.Edit_View')}
                            </ButtonCmp>
                            <ButtonCmp
                                className="text-xs leading-[18px] font-semibold text-grayLight600 !shadow-none !p-0"
                                size="none"
                                icon={<Trash01 className="w-4 h-4 flex-none" />}
                                disabled={isViewChangeLoading}
                                onClick={openDeleteView}
                            >
                                {t('QuotingHub.QuotingDashboard.Delete_View')}
                            </ButtonCmp>
                        </div>
                    ) : undefined
                }
            >
                <TrackOverviewStats />
            </CardCmp>

            {/* ── Order card list ── */}
            <div className={`space-y-3 flex-1 mb-4 -mx-2 px-2 ${!isLoading && !trackList.length ? 'flex flex-col' : 'overflow-y-auto scrollbar-hide'}`}>
                {isLoading ? (
                    <TrackSidebarLoading />
                ) : !isLoading && trackList.length > 0 ? (
                    trackList.map((order, index) => <TrackOrderCard key={order.legDbId} order={order} index={index} onHover={() => onOrderHover?.(order)} onHoverEnd={onOrderHoverEnd} />)
                ) : !isLoading && trackList.length === 0 ? (
                    <div className="h-fit min-h-[192px] bg-white rounded-xl border border-utilityGray200 flex flex-col items-center justify-center">
                        <NotFoundUI
                            title={emptyState.title}
                            desc={emptyState.desc}
                            containerClassName="!h-auto !my-0"
                            iconClassName="!hidden"
                            iconSecondaryClassName="!hidden"
                            iconSecondary={search ? <SearchLg className="w-5 h-5 text-primary" /> : <Package className="w-5 h-5 text-primary" />}
                            buttonText2={search ? t('Radar.Clear_Search') : false}
                            showButtonIcon2={false}
                            descClass="sm:max-w-[400px]"
                            btnClass2="mx-auto"
                            handleActionType2={search ? handleClearSearch : undefined}
                        />
                    </div>
                ) : null}
            </div>

            {isFilterModalOpen && (
                <TrackFilterModal
                    handleClose={() => setIsFilterModalOpen(false)}
                    onApply={applyModalFilters}
                    viewMode={filterModalViewMode}
                    defaultValues={{
                        status: trackParams.status,
                        serviceType: trackParams.serviceType,
                        startDate: trackParams.startDate ? createDateWithoutTimezoneConversion(moment(trackParams.startDate)) ?? null : null,
                        endDate: trackParams.endDate ? createDateWithoutTimezoneConversion(moment(trackParams.endDate)) ?? null : null,
                        refSearch: trackParams.refSearch ?? '',
                        shipperId: trackParams.shipperId ?? null,
                        consigneeId: trackParams.consigneeId ?? null,
                        originSearch: trackParams.originSearch ?? '',
                        destinationSearch: trackParams.destinationSearch ?? '',
                    }}
                />
            )}

            {/* Rename (name popup) — the footer's Rename View action. */}
            {viewAction.rename_view && (
                <CreateViewModal
                    title={viewModalText.title}
                    primaryBtnText={viewModalText.primaryBtnText}
                    handleClose={closeViewAction}
                    handleSave={handleViewModalSave}
                    isLoading={isViewChangeLoading}
                    viewNames={viewNames}
                    modalData={viewAction.data}
                    isError={isError}
                    setIsError={setIsError}
                />
            )}

            {viewAction.delete_view && (
                <DeleteModal
                    moduleTitle={t('QuotingHub.QuotingDashboard.Delete_View')}
                    moduleDeleteModalContent={t('QuotingHub.QuotingDashboard.Delete_View_desc', { View_name: viewAction.data?.view_name })}
                    handleClose={handleDeleteModalClose}
                    dangerBtnText={t('Common.Delete')}
                    isDeleteLoading={isViewChangeLoading}
                />
            )}
        </>
    );
};

export default TrackLeftSidebar;
